'use client'

import { useEffect, useRef, useState, type ImgHTMLAttributes } from 'react'
import { cn } from '#/lib/utils'
import { IMAGE_WIDTHS, isOptimizedPath, pathWithWidth, stemFromLargest } from '#/lib/image-paths'

type ObjectFit = 'cover' | 'contain' | 'fill' | 'none' | 'scale-down'

interface OptimizedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'srcSet' | 'src'> {
  src: string
  lqip?: string
  alt: string
  sizes?: string
  /** className applied to the outer wrapper (sizing, aspect-ratio, rounded, etc.). */
  className?: string
  /** className applied to the inner <img>. Defaults to 'h-full w-full object-cover'. */
  imgClassName?: string
  /** Convenience prop — sets object-fit on the inner img. */
  fit?: ObjectFit
}

const FIT_CLASS: Record<ObjectFit, string> = {
  cover: 'object-cover',
  contain: 'object-contain',
  fill: 'object-fill',
  none: 'object-none',
  'scale-down': 'object-scale-down',
}

export function OptimizedImage({
  src,
  lqip,
  alt,
  sizes,
  className,
  imgClassName,
  fit = 'cover',
  onLoad,
  ...imgProps
}: OptimizedImageProps) {
  const imgRef = useRef<HTMLImageElement>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (imgRef.current?.complete) setLoaded(true)
  }, [src])

  const optimized = isOptimizedPath(src)
  const srcSet = optimized
    ? IMAGE_WIDTHS.map((w) => `${pathWithWidth(stemFromLargest(src), w)} ${w}w`).join(', ')
    : undefined

  return (
    <span className={cn('relative block overflow-hidden', className)}>
      {lqip && (
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-0 transition-opacity duration-500',
            loaded ? 'opacity-0' : 'opacity-100',
          )}
          style={{
            backgroundImage: `url(${lqip})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(20px)',
            transform: 'scale(1.1)',
          }}
        />
      )}
      <img
        ref={imgRef}
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        onLoad={(e) => {
          setLoaded(true)
          onLoad?.(e)
        }}
        className={cn(
          'relative block h-full w-full transition-opacity duration-500',
          FIT_CLASS[fit],
          loaded ? 'opacity-100' : 'opacity-0',
          imgClassName,
        )}
        {...imgProps}
      />
    </span>
  )
}

export default OptimizedImage
